import React, { useEffect, useState, useCallback } from 'react';
import { useModal } from 'src/hooks/Modal';
import api from 'src/services/api';

interface BankAccount {
  id?: string;
  holder_name?: string;
  bank?: string;
  branch?: string;
  branch_check_digit?: string;
  account?: string;
  account_check_digit?: string;
  conta?: string;
  type?: string;
  agencia?: string;
    agencia_dv?: string;
      bank_code?: string;
    conta_dv?: string;
}
 
interface Transfer {
  id: string;
  amount: number;
  status: string;
   
    funding_date: Date;
  funding_estimated_date: Date;
  updated_at?: string;
  fee?: number;
  bank_account?: BankAccount;
  metadata?: Record<string, any>;
  recipient_id?: string;
}

interface TransferPopupProps {
  projectId?: string;
  onClose: () => void;
  title?: string;
}

const fmt = (cents: number) =>
  (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const BANK_NAMES: Record<string, string> = {
  '001': 'Banco do Brasil', '033': 'Santander', '104': 'Caixa Econômica',
  '237': 'Bradesco', '341': 'Itaú', '077': 'Inter', '260': 'Nubank',
  '290': 'PagBank', '323': 'Mercado Pago', '336': 'C6 Bank', '756': 'Sicoob',
};

const bankName = (code?: string) => (code ? (BANK_NAMES[code] ?? `Banco ${code}`) : '—');
const accountType: Record<string, string> = { checking: 'Conta Corrente', savings: 'Conta Poupança', debit: 'Débito' };

interface StatusCfg { label: string; color: string; bg: string; dot: string; icon: string; }

const STATUS: Record<string, StatusCfg> = {
  transferred:   { label: 'Concluído',    color: '#065F46', bg: '#D1FAE5', dot: '#10B981', icon: '✓' },
  pending:       { label: 'Pendente',     color: '#92400E', bg: '#FEF3C7', dot: '#F59E0B', icon: '◔' },
  processing:    { label: 'Processando',  color: '#1E40AF', bg: '#DBEAFE', dot: '#3B82F6', icon: '↻' },
  failed:        { label: 'Falhou',       color: '#7F1D1D', bg: '#FEE2E2', dot: '#EF4444', icon: '✕' },
  canceled:      { label: 'Cancelado',    color: '#374151', bg: '#F3F4F6', dot: '#9CA3AF', icon: '⊘' },
  waiting_funds: { label: 'Aguardando',   color: '#92400E', bg: '#FEF3C7', dot: '#F59E0B', icon: '⏳' },
};

const getStatus = (s: string): StatusCfg =>
  STATUS[s] ?? { label: s, color: '#374151', bg: '#F3F4F6', dot: '#9CA3AF', icon: '?' };

const StatusBadge = ({ status }: { status: string }) => {
  const cfg = getStatus(status);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: '0.03em', color: cfg.color, background: cfg.bg }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.dot, flexShrink: 0, boxShadow: `0 0 0 2px ${cfg.dot}33` }} />
      {cfg.label}
    </span>
  );
};

const DataPill = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
    <span style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
    <span style={{ fontSize: 12, fontWeight: 600, color: '#1E2D40' }}>{value}</span>
  </div>
);

const TransferCard = ({ t, index }: { t: Transfer; index: number }) => {
  const [expanded, setExpanded] = useState(false);
  const cfg = getStatus(t.status);
  const ba = t.bank_account;
  const agencia = ba?.branch ? `${ba.branch}${ba.branch_check_digit ? '-' + ba.branch_check_digit : ''}` : '—';
  const conta   = ba?.conta ?? (ba?.account ? `${ba.account}${ba.account_check_digit ? '-' + ba.account_check_digit : ''}` : '—');
 
 

  return (
    <div className="tf-card" style={{ border: `1px solid #E2E8F0`, borderRadius: 12, background: '#fff',   transition: 'box-shadow 0.15s', animationDelay: `${index * 60}ms`, animationFillMode: 'both', animation: 'slideIn 0.25s ease' }}>
      <div onClick={() => setExpanded(e => !e)} style={{ display: 'grid', gridTemplateColumns: '36px 1fr auto auto auto', alignItems: 'center', gap: 12, padding: '14px 16px', cursor: 'pointer', background: expanded ? '#F8FAFF' : '#fff', transition: 'background 0.12s' }}>

        {/* Ícone status */}
        <div style={{ width: 36, height: 36, borderRadius: 10, background: cfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: cfg.color, fontWeight: 800, flexShrink: 0 }}>
          {cfg.icon}
        </div>

        
 

        {/* Titular + banco */}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#1E2D40', whiteSpace: 'nowrap',   textOverflow: 'ellipsis' }}>
            {`${ba?.bank_code || ba?.bank}: ${ba?.agencia || ba?.branch} ${ba?.agencia_dv || ba?.branch_check_digit} | ${ba?.conta || ba?.account} ${ba?.conta_dv || ba?.account_check_digit}`  }<br/>
        
        <p style={{color:'#333', fontSize:'8px'}}>{t?.metadata?.project_id}</p>
          </div>
          <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 1 }}>
            {bankName(ba?.bank)} · {accountType[ba?.type ?? ''] ?? ba?.type ?? '—'}
          </div>
        </div>

        {/* Status */}
        <StatusBadge status={t.status} />

        {/* ── DATA ── */}
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: '0.01em' }}>
            {new Date(t.funding_date || t.funding_estimated_date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
          </div>
          <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 1 }}>
            {new Date(t.funding_date || t.funding_estimated_date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
          </div>
          {t.fee != null && t.fee > 0 && (
            <div style={{ fontSize: 10, color: '#CBD5E1', marginTop: 2 }}>taxa: {fmt(t.fee)}</div>
          )}
        </div>

        {/* Valor */}
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: t.status === 'failed' ? '#EF4444' : '#065F46', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' }}>
            {fmt(t.amount)}
          </div>
          <div style={{ fontSize: 10, color: expanded ? '#3B82F6' : '#CBD5E1', marginTop: 2, textAlign: 'right', transition: 'color 0.12s' }}>
            {expanded ? '▲ fechar' : '▼ detalhes'}
          </div>
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '12px 16px 16px', borderTop: '1px solid #F1F5F9', background: '#F8FAFF', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {ba && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px 20px', padding: '12px 14px', background: '#fff', borderRadius: 8, border: '1px solid #E2E8F0' }}>
              <DataPill label="Banco"   value={`${bankName(ba.bank)} (${ba.bank ?? '—'})`} />
              <DataPill label="Agência" value={agencia} />
              <DataPill label="Conta"   value={conta} />
              <DataPill label="Tipo"    value={accountType[ba?.type ?? ''] ?? ba?.type ?? '—'} />
              {ba.holder_name && <DataPill label="Titular"  value={ba.holder_name} />}
              {ba.id && <DataPill label="ID Conta" value={<code style={{ fontSize: 10 }}>{ba.id}</code>} />}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div style={{ flex: '1 1 200px', padding: '10px 14px', background: '#fff', borderRadius: 8, border: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <DataPill label="Transfer ID"  value={<code style={{ fontSize: 10.5, wordBreak: 'break-all' }}>{t.id}</code>} />
              {t.recipient_id && <DataPill label="Recipient ID" value={<code style={{ fontSize: 10.5 }}>{t.recipient_id}</code>} />}
              {t.updated_at && <DataPill label="Atualizado em" value={fmtDate(t.updated_at)} />}
            </div>
            {t.metadata && Object.keys(t.metadata).length > 0 && (
              <div style={{ flex: '1 1 200px', padding: '10px 14px', background: '#fff', borderRadius: 8, border: '1px solid #E2E8F0' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Metadata</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {Object.entries(t.metadata).map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', gap: 6, fontSize: 11 }}>
                      <span style={{ color: '#94A3B8', fontWeight: 600 }}>{k}:</span>
                      <span style={{ color: '#1E2D40' }}>{String(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const SummaryBar = ({ transfers }: { transfers: Transfer[] }) => {
  const total      = transfers.reduce((a, t) => a + t.amount, 0);
  const concluidos = transfers.filter(t => t.status === 'transferred');
  const pendentes  = transfers.filter(t => ['pending', 'processing', 'waiting_funds'].includes(t.status));
  const falhos     = transfers.filter(t => ['failed', 'canceled'].includes(t.status));
  const totalFees  = transfers.reduce((a, t) => a + (t.fee ?? 0), 0);
  const items = [
    { label: 'Total movimentado', value: fmt(total),                                                                          color: '#1E3A5F' },
    { label: 'Concluídos',        value: `${fmt(concluidos.reduce((a,t)=>a+t.amount,0))} (${concluidos.length})`,            color: '#065F46' },
    { label: 'Pendentes',         value: `${fmt(pendentes.reduce((a,t)=>a+t.amount,0))} (${pendentes.length})`,              color: '#92400E' },
    { label: 'Falhos/Cancelados', value: `${fmt(falhos.reduce((a,t)=>a+t.amount,0))} (${falhos.length})`,                   color: '#7F1D1D' },
    { label: 'Taxas cobradas',    value: fmt(totalFees),                                                                      color: '#6B7280' },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 1, background: '#E2E8F0', borderRadius: 10, overflow: 'hidden', border: '1px solid #E2E8F0' }}>
      {items.map((it, i) => (
        <div key={i} style={{ padding: '12px 16px', background: '#fff', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{it.label}</span>
          <span style={{ fontSize: 14, fontWeight: 800, color: it.color, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>{it.value}</span>
        </div>
      ))}
    </div>
  );
};

type FilterStatus = 'all' | 'transferred' | 'pending' | 'processing' | 'failed' | 'canceled';

const FilterBar = ({ active, onChange, counts }: { active: FilterStatus; onChange: (f: FilterStatus) => void; counts: Record<string, number> }) => {
  const opts: Array<{ id: FilterStatus; label: string }> = [
    { id: 'all', label: 'Todos' }, { id: 'transferred', label: 'Concluído' }, { id: 'pending', label: 'Pendente' },
    { id: 'processing', label: 'Processando' }, { id: 'failed', label: 'Falhou' }, { id: 'canceled', label: 'Cancelado' },
  ];
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {opts.map(o => {
        const count = o.id === 'all' ? Object.values(counts).reduce((a, b) => a + b, 0) : (counts[o.id] ?? 0);
        const isActive = active === o.id;
        const cfg = o.id === 'all' ? null : STATUS[o.id];
        return (
          <button key={o.id} onClick={() => onChange(o.id)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 20, border: isActive ? '1.5px solid #1E3A5F' : '1.5px solid #E2E8F0', background: isActive ? '#1E3A5F' : '#fff', color: isActive ? '#fff' : '#64748B', fontWeight: 600, fontSize: 11.5, cursor: 'pointer', transition: 'all 0.12s' }}>
            {cfg && <span style={{ width: 6, height: 6, borderRadius: '50%', background: isActive ? '#fff' : cfg.dot }} />}
            {o.label}
            {count > 0 && <span style={{ fontSize: 10, fontWeight: 800, background: isActive ? 'rgba(255,255,255,0.2)' : '#F1F5F9', color: isActive ? '#fff' : '#94A3B8', borderRadius: 999, padding: '0 5px' }}>{count}</span>}
          </button>
        );
      })}
    </div>
  );
};

export const TransferPopup: React.FC<TransferPopupProps> = ({ projectId, onClose, title = 'Saques do Projeto' }) => {
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [filter, setFilter]       = useState<FilterStatus>('all');
  const [search, setSearch]       = useState('');
  const [sortDesc, setSortDesc]   = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(projectId ? `/list-transfers/${projectId}` : `/list-transfers/`);
      setTransfers(res?.data?.rows ?? []);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Erro ao carregar os saques. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const filtered = transfers
    .filter(t => {
      if (filter !== 'all' && t.status !== filter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        return (
          t.id?.toLowerCase().includes(q) ||
          t.bank_account?.holder_name?.toLowerCase().includes(q) ||
          t.bank_account?.conta?.toLowerCase().includes(q) ||
          t.bank_account?.account?.toLowerCase().includes(q) ||
          t.status?.toLowerCase().includes(q) ||
          String(t.metadata?.project_id ?? '').toLowerCase().includes(q)
        );
      }
      return true;
    })
    .sort((a, b) => {
      const ta = new Date(a.funding_date || a.funding_estimated_date).getTime();
      const tb = new Date(b.funding_date || b.funding_estimated_date).getTime();
      return sortDesc ? tb - ta : ta - tb;
    });

  const counts: Record<string, number> = {};
  for (const t of transfers) { counts[t.status] = (counts[t.status] ?? 0) + 1; }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Sora:wght@400;600;700;800&display=swap');
        .tfpopup * { box-sizing: border-box; font-family: 'Sora', sans-serif; }
        @keyframes slideIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes backdropIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes panelIn { from { opacity: 0; transform: translateY(20px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        .tf-card:hover { box-shadow: 0 2px 16px rgba(30,58,95,0.08); }
        .tf-scroll::-webkit-scrollbar { width: 5px; }
        .tf-scroll::-webkit-scrollbar-track { background: #F8FAFF; }
        .tf-scroll::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 99px; }
        .tf-scroll::-webkit-scrollbar-thumb:hover { background: #94A3B8; }
      `}</style>

      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(4px)', animation: 'backdropIn 0.2s ease' }} className="tfpopup" />

      <div className="tfpopup" style={{ position: 'fixed', zIndex: 1001, top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '92vw', maxWidth: 780, maxHeight: '88vh', background: '#F8FAFF', borderRadius: 18, boxShadow: '0 24px 80px rgba(15,23,42,0.28), 0 0 0 1px rgba(30,58,95,0.08)', display: 'flex', flexDirection: 'column', animation: 'panelIn 0.22s cubic-bezier(0.34,1.56,0.64,1)', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '18px 22px 16px', background: '#fff', borderBottom: '1px solid #E9EFF7', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg, #1E3A5F, #2563EB)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>🏦</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em' }}>{title}</h2>
            <p style={{ margin: 0, fontSize: 12, color: '#94A3B8', marginTop: 1 }}>
              Transferências bancárias via Pagar.me · projeto: <code style={{ fontFamily: 'DM Mono, monospace', fontSize: 11 }}>{projectId}</code>
            </p>
          </div>
          <button onClick={load} disabled={loading} title="Atualizar" style={{ width: 34, height: 34, borderRadius: 8, border: '1px solid #E2E8F0', background: '#fff', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#64748B', flexShrink: 0, opacity: loading ? 0.5 : 1 }}>
            <span style={{ display: 'inline-block', animation: loading ? 'spin 0.7s linear infinite' : 'none' }}>↺</span>
          </button>
          <button onClick={onClose} style={{ width: 34, height: 34, borderRadius: 8, border: '1px solid #E2E8F0', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: '#94A3B8', flexShrink: 0, lineHeight: 1 }}>×</button>
        </div>

        {/* Conteúdo */}
        <div className="tf-scroll" style={{ flex: 1, overflowY: 'auto', padding: '16px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {loading && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 200, gap: 12, color: '#94A3B8' }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', border: '3px solid #E2E8F0', borderTopColor: '#1E3A5F', animation: 'spin 0.7s linear infinite' }} />
              <span style={{ fontSize: 13 }}>Carregando saques…</span>
            </div>
          )}

          {!loading && error && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 160, gap: 12 }}>
              <span style={{ fontSize: 28 }}>⚠️</span>
              <p style={{ color: '#EF4444', fontWeight: 600, fontSize: 14, margin: 0, textAlign: 'center' }}>{error}</p>
              <button onClick={load} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#1E3A5F', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Tentar novamente</button>
            </div>
          )}

          {!loading && !error && (
            <>
              {transfers.length > 0 && <SummaryBar transfers={transfers} />}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ flex: '1 1 200px', position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: '#CBD5E1', pointerEvents: 'none' }}>🔍</span>
                  <input placeholder="Buscar por titular, conta, ID…" value={search} onChange={e => setSearch(e.target.value)} style={{ width: '100%', padding: '8px 10px 8px 32px', borderRadius: 8, border: '1.5px solid #E2E8F0', background: '#fff', fontSize: 12.5, color: '#1E2D40', fontFamily: 'Sora, sans-serif', outline: 'none' }} />
                </div>
                <button onClick={() => setSortDesc(s => !s)} style={{ padding: '7px 14px', borderRadius: 8, border: '1.5px solid #E2E8F0', background: '#fff', color: '#64748B', fontWeight: 600, fontSize: 11.5, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0, fontFamily: 'Sora, sans-serif' }}>
                  {sortDesc ? '↓' : '↑'} {sortDesc ? 'Mais recentes' : 'Mais antigos'}
                </button>
              </div>
              <FilterBar active={filter} onChange={setFilter} counts={counts} />
              {filtered.length === 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 120, gap: 8 }}>
                  <span style={{ fontSize: 28 }}>{transfers.length === 0 ? '📭' : '🔍'}</span>
                  <p style={{ color: '#94A3B8', fontSize: 13, margin: 0, textAlign: 'center' }}>
                    {transfers.length === 0 ? 'Nenhuma transferência encontrada para este projeto.' : 'Nenhum resultado para os filtros aplicados.'}
                  </p>
                  {search && <button onClick={() => setSearch('')} style={{ fontSize: 12, color: '#3B82F6', border: 'none', background: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Limpar busca</button>}
                </div>
              )}
              {filtered.map((t, i) => <TransferCard key={t.id} t={t} index={i} />)}
            </>
          )}
        </div>

        {/* Footer */}
        {!loading && !error && transfers.length > 0 && (
          <div style={{ padding: '11px 22px', borderTop: '1px solid #E9EFF7', background: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: 11.5, color: '#94A3B8' }}>
              {filtered.length !== transfers.length ? `${filtered.length} de ${transfers.length} saque${transfers.length !== 1 ? 's' : ''}` : `${transfers.length} saque${transfers.length !== 1 ? 's' : ''} no total`}
            </span>
            <span style={{ fontSize: 11, color: '#CBD5E1' }}>ESC para fechar</span>
          </div>
        )}
      </div>
    </>
  );
};

export function useTransferPopup() {
  const { addModal, removeModal } = useModal();

  const open  = (project_id = '') => addModal({
    title: '',
    content: <TransferPopup projectId={project_id} title={'Saques'} onClose={close} />,
    theme: 'whiteModal',
    key: 'finance-modal',
  });
  const close = () => removeModal('finance-modal');

  return { open, close };
}

export default TransferPopup;
